import { supabase } from '@/integrations/supabase/client';

export interface Project {
  id: string;
  slug: string;
  title: string;
  category: "photography" | "videography" | "social-media";
  description: string;
  fullDescription?: string | undefined;
  coverImage: string;
  images?: string[] | undefined;
  client?: string | undefined;
  year: string;
  services?: string[] | undefined;
  hashtags?: string[] | undefined;
  industry?: string | undefined;
  isPublic?: boolean | undefined;
  language: 'de' | 'en';
}

export interface LeanProject {
  id: string;
  slug: string;
  title: string;
  category: "photography" | "videography" | "social-media";
  description: string;
  client?: string | undefined;
  year: string;
  industry?: string | undefined;
  isPublic?: boolean | undefined;
  created_at?: string | undefined;
  language: 'de' | 'en';
}

export const supabaseProjectManager = {
  async getProjectsLean(options: { 
    publicOnly?: boolean; 
    limit?: number 
  } = {}): Promise<LeanProject[]> {
    const { publicOnly = false, limit } = options;
    
    let query = supabase
      .from('projects')
      .select('id, slug, title, category, description, client, year, industry, is_public, created_at, language')
      .order('created_at', { ascending: false });

    if (publicOnly) {
      query = query.eq('is_public', true);
    }

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching lean projects:', error);
      throw error;
    }

    return data.map(project => ({
      id: project.id,
      slug: project.slug,
      title: project.title,
      category: project.category as "photography" | "videography" | "social-media",
      description: project.description,
      client: project.client || undefined,
      year: project.year,
      industry: project.industry || undefined,
      isPublic: project.is_public || false,
      created_at: project.created_at,
      language: project.language as 'de' | 'en'
    }));
  },

  async getCoverImage(projectId: string): Promise<{ coverImage?: string; images?: string[] } | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('cover_image, images')
      .eq('id', projectId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching cover image:', error);
      return null;
    }

    if (!data) return null;

    return {
      coverImage: data.cover_image,
      images: data.images || []
    };
  },

  async getProjects(publicOnly: boolean = false): Promise<Project[]> {
    let query = supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });

    // If publicOnly is true, filter for public projects only
    if (publicOnly) {
      query = query.eq('is_public', true);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }

    return data.map(project => ({
      id: project.id,
      slug: project.slug,
      title: project.title,
      category: project.category as "photography" | "videography" | "social-media",
      description: project.description,
      fullDescription: project.full_description || undefined,
      coverImage: project.cover_image,
      images: project.images || [],
      client: project.client || undefined,
      year: project.year,
      services: project.services || [],
      hashtags: project.hashtags || [],
      industry: project.industry || undefined,
      isPublic: project.is_public || false,
      language: project.language as 'de' | 'en'
    }));
  },

  async getProject(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching project:', error);
      return null;
    }

    if (!data) return null;

    return {
      id: data.id,
      slug: data.slug,
      title: data.title,
      category: data.category as "photography" | "videography" | "social-media",
      description: data.description,
      fullDescription: data.full_description || undefined,
      coverImage: data.cover_image,
      images: data.images || [],
      client: data.client || undefined,
      year: data.year,
      services: data.services || [],
      hashtags: data.hashtags || [],
      industry: data.industry || undefined,
      isPublic: data.is_public || false,
      language: data.language as 'de' | 'en'
    };
  },

  async getProjectBySlug(slug: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('slug', slug)
      .eq('is_public', true)
      .maybeSingle();

    if (error) {
      console.error('Error fetching project by slug:', error);
      return null;
    }

    if (!data) return null;

    return {
      id: data.id,
      slug: data.slug,
      title: data.title,
      category: data.category as "photography" | "videography" | "social-media",
      description: data.description,
      fullDescription: data.full_description || undefined,
      coverImage: data.cover_image,
      images: data.images || [],
      client: data.client || undefined,
      year: data.year,
      services: data.services || [],
      hashtags: data.hashtags || [],
      industry: data.industry || undefined,
      isPublic: data.is_public || false,
      language: data.language as 'de' | 'en'
    };
  },

  async addProject(project: Omit<Project, 'id'>): Promise<Project> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to add projects');
    }

    const { data, error } = await supabase
      .from('projects')
      .insert([{
        user_id: user.id,
        slug: project.slug,
        title: project.title,
        category: project.category,
        description: project.description,
        full_description: project.fullDescription || null,
        cover_image: project.coverImage,
        images: project.images || [],
        client: project.client || null,
        year: project.year,
        services: project.services || [],
        hashtags: project.hashtags || [],
        industry: project.industry || null,
        is_public: project.isPublic || false,
        language: project.language || 'de'
      }])
      .select()
      .single();

    if (error) {
      console.error('Error adding project:', error);
      throw error;
    }

    return {
      id: data.id,
      slug: data.slug,
      title: data.title,
      category: data.category as "photography" | "videography" | "social-media",
      description: data.description,
      fullDescription: data.full_description || undefined,
      coverImage: data.cover_image,
      images: data.images || [],
      client: data.client || undefined,
      year: data.year,
      services: data.services || [],
      hashtags: data.hashtags || [],
      industry: data.industry || undefined,
      isPublic: data.is_public || false,
      language: data.language as 'de' | 'en'
    };
  },

  async updateProject(id: string, updates: Partial<Project>): Promise<Project | null> {
    // Fetch current project to detect visibility changes
    const { data: currentProject } = await supabase
      .from('projects')
      .select('is_public')
      .eq('id', id)
      .single();

    const { data, error } = await supabase
      .from('projects')
      .update({
        ...(updates.slug && { slug: updates.slug }),
        ...(updates.title && { title: updates.title }),
        ...(updates.category && { category: updates.category }),
        ...(updates.description && { description: updates.description }),
        ...(updates.fullDescription !== undefined && { full_description: updates.fullDescription }),
        ...(updates.coverImage && { cover_image: updates.coverImage }),
        ...(updates.images !== undefined && { images: updates.images }),
        ...(updates.client !== undefined && { client: updates.client }),
        ...(updates.year && { year: updates.year }),
        ...(updates.services !== undefined && { services: updates.services }),
        ...(updates.hashtags !== undefined && { hashtags: updates.hashtags }),
        ...(updates.industry !== undefined && { industry: updates.industry }),
        ...(updates.isPublic !== undefined && { is_public: updates.isPublic }),
        ...(updates.language && { language: updates.language })
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating project:', error);
      return null;
    }

    if (!data) return null;

    // Log visibility changes for security audit
    if (updates.isPublic !== undefined && currentProject && currentProject.is_public !== updates.isPublic) {
      try {
        await supabase.rpc('log_audit', {
          p_action: updates.isPublic ? 'project_published' : 'project_unpublished',
          p_resource_type: 'project',
          p_resource_id: id,
          p_metadata: {
            title: data.title,
            old_visibility: currentProject.is_public,
            new_visibility: updates.isPublic
          }
        });
      } catch (logError) {
        console.error('Failed to log project visibility change:', logError);
      }
    }

    return {
      id: data.id,
      slug: data.slug,
      title: data.title,
      category: data.category as "photography" | "videography" | "social-media",
      description: data.description,
      fullDescription: data.full_description || undefined,
      coverImage: data.cover_image,
      images: data.images || [],
      client: data.client || undefined,
      year: data.year,
      services: data.services || [],
      hashtags: data.hashtags || [],
      industry: data.industry || undefined,
      isPublic: data.is_public || false,
      language: data.language as 'de' | 'en'
    };
  },

  async deleteProject(id: string): Promise<boolean> {
    // Fetch project details before deletion for audit log
    const { data: projectData } = await supabase
      .from('projects')
      .select('title, category')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting project:', error);
      return false;
    }

    // Log project deletion for security audit
    if (projectData) {
      try {
        await supabase.rpc('log_audit', {
          p_action: 'project_deleted',
          p_resource_type: 'project',
          p_resource_id: id,
          p_metadata: {
            title: projectData.title,
            category: projectData.category
          }
        });
      } catch (logError) {
        console.error('Failed to log project deletion:', logError);
      }
    }

    return true;
  },

  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }
};
